import { useRef, useCallback, useEffect } from 'react';
import type { ReplayRequest, ReplayResponseMessage } from '../types';

type PendingRequest = {
  resolve: (value: ReplayResponseMessage) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
};

export function useFixWebSocket(
  onMessage: (msg: string) => void,
  onStatusChange?: (status: 'connecting' | 'connected' | 'disconnected') => void,
  url = import.meta.env.VITE_WS_URL,
  onReconnect?: () => void,
  token?: string | null,
  onAuthFailure?: () => void
) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isConnectingRef = useRef(false);
  const isMountedRef = useRef(false);
  const pendingRequestsRef = useRef<Map<string, PendingRequest>>(new Map());
  const lastMessageTimestampsRef = useRef<Map<string, number>>(new Map());
  const wasConnectedRef = useRef(false);
  const recentPayloadRef = useRef<Map<string, number>>(new Map());

  const onMessageRef = useRef(onMessage);
  const onStatusRef = useRef(onStatusChange);
  const onReconnectRef = useRef(onReconnect);
  const onAuthFailureRef = useRef(onAuthFailure);
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);
  useEffect(() => { onStatusRef.current = onStatusChange; }, [onStatusChange]);
  useEffect(() => { onReconnectRef.current = onReconnect; }, [onReconnect]);
  useEffect(() => { onAuthFailureRef.current = onAuthFailure; }, [onAuthFailure]);

  const notifyStatus = (s: 'connecting' | 'connected' | 'disconnected') => {
    onStatusRef.current?.(s);
  };

  const cleanupTimers = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  };

  const connect = useCallback(() => {
    if (isConnectingRef.current || wsRef.current?.readyState === WebSocket.OPEN) return;
    if (!isMountedRef.current) return;

    if (!url) {
      notifyStatus('disconnected');
      return;
    }

    isConnectingRef.current = true;
    notifyStatus('connecting');

    try {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      const normalizedUrl = url.trim().replace(/\/$/, '');
      if (!normalizedUrl.startsWith('ws://') && !normalizedUrl.startsWith('wss://')) {

        isConnectingRef.current = false;
        notifyStatus('disconnected');
        return;
      }

      // Connect WITHOUT the token in the URL. The JWT is sent as the first WS
      // frame instead (server requires { type:'auth', token } when no URL token),
      // which keeps the credential out of nginx access logs, Referer, and history.
      const ws = new WebSocket(normalizedUrl);

      ws.onopen = () => {
        if (!isMountedRef.current) return;
        isConnectingRef.current = false;
        wsRef.current = ws;
        // Authenticate as the very first frame, before any other message.
        if (token) {
          try {
            ws.send(JSON.stringify({ type: 'auth', token }));
          } catch {
            /* ignore; onerror/onclose will handle a dead socket */
          }
        }
        cleanupTimers();
        notifyStatus('connected');
        // console.log('[WebSocket] ✅ Connected successfully');
        
        // Always trigger recovery on connection (both initial and reconnect)
        // Give it a moment to ensure the connection is fully established
        setTimeout(() => {
          if (onReconnectRef.current && isMountedRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
            const isReconnect = wasConnectedRef.current;
            // console.log(`[WebSocket] 🔄 Triggering ${isReconnect ? 'reconnect' : 'initial'} recovery...`);
            onReconnectRef.current();
          } else if (onReconnectRef.current) {
            // Recovery callback exists but WebSocket might not be ready yet - this is okay
            // console.log('[WebSocket] Recovery callback available but WebSocket not ready yet');
          }
        }, 1000);
        
        wasConnectedRef.current = true;
      };

      ws.onmessage = (event) => {
        if (!isMountedRef.current) return;
        
        // Log all incoming messages for debugging (commented out for production)
        // const msgPreview = typeof event.data === 'string' 
        //   ? (event.data.length > 200 ? event.data.substring(0, 200) + '...' : event.data)
        //   : '[Binary or non-string data]';
        // console.log('[WebSocket] 📨 Received message:', msgPreview);
        
        if (event.data && typeof event.data === 'string' && event.data.trim().startsWith('{')) {
          try {
            const jsonMsg = JSON.parse(event.data);
            
            // Log order-related messages specifically
            // if (jsonMsg.type === 'ORDER_ACK' || jsonMsg.type === 'EXECUTION_REPORT' || jsonMsg.type === 'ORDER_REJECT' || jsonMsg.type === 'PLACE_ORDER') {
            //   console.log('[WebSocket] 🎯 Order-related message detected:', jsonMsg);
            // }
            
            if (jsonMsg.request_id && pendingRequestsRef.current.has(jsonMsg.request_id)) {
              const pending = pendingRequestsRef.current.get(jsonMsg.request_id)!;
              pendingRequestsRef.current.delete(jsonMsg.request_id);
              clearTimeout(pending.timeout);
              
              if (jsonMsg.type?.endsWith('_ERROR')) {
                pending.reject(new Error(jsonMsg.error || 'Replay request failed'));
              } else {
                pending.resolve(jsonMsg as ReplayResponseMessage);
              }
              return;
            }

            // Drop fast duplicate websocket payloads from multi-forward paths.
            const msgKind = String(jsonMsg.kind || jsonMsg.type || '').toUpperCase();
            if (msgKind) {
              const now = Date.now();
              const lastSeenAt = recentPayloadRef.current.get(event.data) || 0;
              if (lastSeenAt > 0 && (now - lastSeenAt) < 500) {
                return;
              }
              recentPayloadRef.current.set(event.data, now);
              if (recentPayloadRef.current.size > 1500) {
                const entries = Array.from(recentPayloadRef.current.entries());
                for (const [k, t] of entries) {
                  if ((now - t) > 5000) recentPayloadRef.current.delete(k);
                }
              }
            }
            
            if (jsonMsg.kind && jsonMsg.timestamp) {
              const stream = getStreamForKind(jsonMsg.kind);
              if (stream) {
                const ts = parseTimestamp(jsonMsg.timestamp);
                if (ts) {
                  const current = lastMessageTimestampsRef.current.get(stream) || 0;
                  if (ts > current) {
                    lastMessageTimestampsRef.current.set(stream, ts);
                  }
                }
              }
            }
          } catch (error) {
            // console.warn('[WebSocket] ⚠️ Failed to parse JSON message:', error);
          }
        }
        
        onMessageRef.current(event.data);
      };

      ws.onerror = (error) => {
        if (!isMountedRef.current) return;
        isConnectingRef.current = false;
        notifyStatus('disconnected');
        console.error('[WebSocket] ❌ Connection error:', error);
      };

      ws.onclose = (event) => {
        if (!isMountedRef.current) return;
        isConnectingRef.current = false;
        notifyStatus('disconnected');
        wsRef.current = null;
        // console.log('[WebSocket] 🔌 Connection closed. Code:', event.code, 'Reason:', event.reason || 'No reason provided');
        
        if (event.code === 1008) {
          console.error('[WebSocket] ❌ Connection rejected by server. Reason:', event.reason);
          console.error('   - This usually means authentication failed');
          console.error('   - Check if token is valid and WebSocket server has auth enabled');
          onAuthFailureRef.current?.();
          // Don't auto-reconnect if auth failed - user needs to log in again
          return;
        } else if (event.code === 1006) {
          console.error('[WebSocket] 💡 Connection closed abnormally. Possible causes:');
          console.error('   - WebSocket server is not running');
          console.error('   - Network/firewall blocking the connection');
          console.error('   - Server rejected connection (check server logs)');
        } else if (event.code === 1002) {
          console.error('[WebSocket] 💡 Protocol error. Check that the URL is correct and the server supports WebSocket.');
        } else if (event.code === 1003) {
          console.error('[WebSocket] 💡 Unsupported data type. The server may not support the WebSocket protocol.');
        } else if (event.code === 1000) {
          // console.log('[WebSocket] ✅ Normal closure');
          return; // Don't reconnect on normal closure
        }

        // attempt reconnect unless normal close
        if (isMountedRef.current && event.code !== 1000) {
          // console.log('[WebSocket] 🔄 Will attempt to reconnect in 3 seconds...');
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) connect();
          }, 3000);
        }
      };

      wsRef.current = ws;

      setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          // console.warn('[WebSocket] ⚠️ Connection timeout after 10 seconds. Closing...');
          try { ws.close(); } catch {}
        }
      }, 10000);
    } catch {
      isConnectingRef.current = false;
      notifyStatus('disconnected');
      if (isMountedRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) connect();
        }, 3000);
      }
    }
  }, [url, token]);

  useEffect(() => {
    isMountedRef.current = true;
    // Only connect once we have an auth token: the server requires the first WS frame
    // to be { type:'auth', token }, so connecting without one is immediately rejected
    // ("First message must be auth"). Re-runs when `token` loads (dep array below).
    if (token) {
      connect();
    }
    return () => {
      isMountedRef.current = false;
      cleanupTimers();
      try { wsRef.current?.close(); } catch {}
      wsRef.current = null;
      isConnectingRef.current = false;
    };
  }, [url, token, connect]);

  const sendFixMessage = useCallback((msg: string) => {
    // console.log('[WebSocket] 🔍 sendFixMessage called');
    // console.log('[WebSocket] 🔍 wsRef.current:', wsRef.current);
    // console.log('[WebSocket] 🔍 readyState:', wsRef.current?.readyState);
    // console.log('[WebSocket] 🔍 OPEN constant:', WebSocket.OPEN);
    
    if (!wsRef.current) {
      console.error('[WebSocket] ❌ wsRef.current is null!');
      return false;
    }
    
    if (wsRef.current.readyState === WebSocket.OPEN) {
      try { 
        // console.log('[WebSocket] ✅ WebSocket is OPEN, sending message...');
        wsRef.current.send(msg);
        // Log sent messages (truncate if too long)
        // const preview = msg.length > 200 ? msg.substring(0, 200) + '...' : msg;
        // console.log('[WebSocket] 📤 Message sent successfully:', preview);
        // console.log('[WebSocket] 📤 Full message:', msg);
        return true; 
      } catch (error) {
        console.error('[WebSocket] ❌ Error sending message:', error);
        console.error('[WebSocket] ❌ Error details:', {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined
        });
        return false;
      }
    } else {
      const stateNames = {
        [WebSocket.CONNECTING]: 'CONNECTING',
        [WebSocket.OPEN]: 'OPEN',
        [WebSocket.CLOSING]: 'CLOSING',
        [WebSocket.CLOSED]: 'CLOSED'
      };
      const stateName = stateNames[wsRef.current.readyState as keyof typeof stateNames] || 'UNKNOWN';
      console.warn('[WebSocket] ⚠️ Cannot send message - WebSocket not open.');
      console.warn('[WebSocket] ⚠️ Current state:', wsRef.current.readyState, `(${stateName})`);
      // console.warn('[WebSocket] ⚠️ Message that would have been sent:', msg.substring(0, 200));
      return false;
    }
  }, []);

  const sendReplayRequest = useCallback((request: Omit<ReplayRequest, 'request_id'>): Promise<ReplayResponseMessage> => {
    return new Promise((resolve, reject) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const fullRequest: ReplayRequest = { ...request, request_id: requestId };

      const timeout = setTimeout(() => {
        if (pendingRequestsRef.current.has(requestId)) {
          pendingRequestsRef.current.delete(requestId);
          reject(new Error('Request timeout after 30 seconds'));
        }
      }, 30000);

      pendingRequestsRef.current.set(requestId, { resolve, reject, timeout });

      try {
        wsRef.current.send(JSON.stringify(fullRequest));
        // console.log('[WebSocket] 📤 Sent replay request:', request.type, request.stream);
      } catch (error) {
        pendingRequestsRef.current.delete(requestId);
        clearTimeout(timeout);
        reject(error);
      }
    });
  }, []);

  const getLastMessageTimestamp = useCallback((stream: string): number | undefined => {
    return lastMessageTimestampsRef.current.get(stream);
  }, []);

  const clearLastMessageTimestamps = useCallback(() => {
    lastMessageTimestampsRef.current.clear();
  }, []);

  // Helper functions
  function getStreamForKind(kind: string): string | null {
    const mapping: Record<string, string> = {
      'BOOK_CHANGES': 'fix:bookchanges',
      'MARKET_STATS': 'fix:marketstats',
      'TWAP': 'fix:twap',
      'VWAMP': 'fix:vwamp',
      'MICROPRICE': 'fix:vwamp',
      'POSITION_UPDATE': 'fix:positions',
      'TRADE': 'fix:trades',
      'EXECUTION_REPORT': 'fix:trades',
      'ORDER_ACK': 'fix:orders',
      'ORDER_REJECT': 'fix:orders',
      'PLACE_ORDER': 'fix:orders',
    };
    return mapping[kind] || null;
  }

  function parseTimestamp(ts: string | number): number | null {
    if (typeof ts === 'number') {
      return ts < 1e12 ? ts * 1000 : ts;
    }
    if (typeof ts === 'string') {
      const parsed = Date.parse(ts);
      if (Number.isFinite(parsed)) return parsed;
      const num = parseFloat(ts);
      if (Number.isFinite(num)) {
        return num < 1e12 ? num * 1000 : num;
      }
    }
    return null;
  }

  // Test function to verify WebSocket connection
  const testConnection = useCallback(() => {
    // console.log('[WebSocket] 🧪 Testing connection...');
    // console.log('[WebSocket] 🧪 wsRef.current:', wsRef.current);
    // console.log('[WebSocket] 🧪 readyState:', wsRef.current?.readyState);
    // console.log('[WebSocket] 🧪 OPEN:', WebSocket.OPEN);
    
    if (!wsRef.current) {
      // console.error('[WebSocket] ❌ No WebSocket instance!');
      return false;
    }
    
    if (wsRef.current.readyState === WebSocket.OPEN) {
      const testMsg = JSON.stringify({ type: 'TEST', message: 'Connection test', timestamp: Date.now() });
      try {
        wsRef.current.send(testMsg);
        // console.log('[WebSocket] ✅ Test message sent:', testMsg);
        return true;
      } catch (error) {
        // console.error('[WebSocket] ❌ Failed to send test message:', error);
        return false;
      }
    } else {
      // console.warn('[WebSocket] ⚠️ WebSocket not open for testing');
      return false;
    }
  }, []);

  return { 
    sendFixMessage, 
    sendReplayRequest,
    getLastMessageTimestamp,
    clearLastMessageTimestamps,
    testConnection,
    isConnected: wsRef.current?.readyState === WebSocket.OPEN
  };
}
